import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DeviceHelper } from '../../../../helpers/deviceHelper';
import ModalDialog from '../../Dialog/ModalDialog';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '../../TextField/TextField';
import SelectField from '../../SelectField/SelectField';
import isEmpty from '../../../../is-empty';
import ValueHelper from '../../../../helpers/valueHelper';
import common from '../../../../jsons/common.json';
import PrintPreview from '../../../Devices/PrintPreview';

const initialState = {
  errors: {},
  labelData: {},
  deviceList: [],
  selectedDevice: {},
  printer: {},
  count: 1,
  pauseCount: 0,
  ready: false,
  replica: 0,
  override: 'N',
  qualityList: [],
  sendToPrint: '',
  enablePrint: true,
  enablePreview: false,
  printPreview: false,
  dimensions: null,
  templateList: [],
  selectedTemplate: null,
  templatesLoading: true,
};

function Labels() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const devices = useSelector((state) => state.devices);

  const [state, setState] = useState(initialState);

  // Map dpmm quality label to DPI. Template dpi is stored as one of
  // [203, 300, 600] (see labelTemplate model), so snap the computed
  // value to the nearest supported enum rather than using the raw
  // Math.round(dpmm * 25.4) result (which yields 305/610 for 12/24 dpmm).
  const dpmmToDpi = (qualityLabel) => {
    if (!qualityLabel) return null;
    const match = qualityLabel.match(/(\d+)\s*dpmm/i);
    if (!match) return null;
    const dpmm = parseInt(match[1]);
    const raw = dpmm * 25.4;
    const supported = [203, 300, 600];
    return supported.reduce((best, dpi) =>
      Math.abs(dpi - raw) < Math.abs(best - raw) ? dpi : best
    );
  };

  // Fetch templates for this label type (and related types for container)
  useEffect(() => {
    if (!devices.labelType) return;
    let cancelled = false;
    setState(prev => ({ ...prev, templatesLoading: true, selectedTemplate: null }));
    (async () => {
      try {
        const types = [devices.labelType];
        // Container screens should also show Sample and Lot templates
        const containerType = common.labelPrintTypes.container;
        if (devices.labelType === containerType) {
          types.push(common.labelPrintTypes.sample, common.labelPrintTypes.lot);
        }
        const responses = await Promise.all(
          types.map(type => axios.get('/api/labeltemplate/type/' + type).catch(() => ({ data: [] })))
        );
        if (cancelled) return;
        const combined = responses.flatMap(r => r.data || []);
        setState(prev => ({
          ...prev,
          templateList: combined,
          templatesLoading: false,
        }));
      } catch {
        if (!cancelled) {
          setState(prev => ({ ...prev, templateList: [], templatesLoading: false }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [devices.labelType]);

  // Filter template options by printer DPI
  const printerDpi = dpmmToDpi(state.printer?.qualitySel?.label);
  const templateOptions = useMemo(() => {
    let filtered = state.templateList;
    if (printerDpi) {
      filtered = filtered.filter(tpl => tpl.dpi === printerDpi);
    }
    return filtered.map(tpl => ({ label: tpl.name, value: tpl._id }));
  }, [state.templateList, printerDpi]);

  // Read the device's saved label config for the current print type
  const deviceLabelConfig = useMemo(() => {
    const labels = state.printer?.labels;
    if (!labels || !devices.labelType) return null;
    return labels.find(l => l.type === devices.labelType) || null;
  }, [state.printer?.labels, devices.labelType]);

  // Auto-select template and count from saved device config
  useEffect(() => {
    if (state.templatesLoading || state.templateList.length === 0 || !deviceLabelConfig) return;
    const savedTemplateId = deviceLabelConfig.templateId;
    if (!savedTemplateId) return;
    const tpl = state.templateList.find(t => t._id === savedTemplateId);
    if (!tpl) return;
    setState(prev => {
      if (prev.selectedTemplate?._id === savedTemplateId) return prev;
      return {
        ...prev,
        selectedTemplate: tpl,
        enablePreview: true,
        count: deviceLabelConfig.promptForCount ? (deviceLabelConfig.count ?? 1) : (deviceLabelConfig.count ?? 1),
      };
    });
  }, [state.templatesLoading, state.templateList, deviceLabelConfig]);

  const loadRecord = useCallback(() => {
    const registered = devices?.registered ?? [];
    let dList = [];
    let selectedDevice = {};
    let printer = {};
    if (!isEmpty(registered) && registered.length > 0) {
      registered.forEach((device) => {
        dList.push({
          label: device.name,
          value: device.name.toLowerCase(),
          quality: device.quality != null ? device.quality : null,
          pauseCount: device.pauseCount != null ? device.pauseCount : 0,
        });
      });
      if (!isEmpty(devices.defaultDevice)) {
        selectedDevice = {
          label: devices.defaultDevice.name,
          value: devices.defaultDevice.name.toLowerCase(),
        };
        printer = { ...devices.defaultDevice };
        const qualitySel = state.qualityList.find((q) => q.value === printer.quality);
        printer.qualitySel = qualitySel;
      }
    }

    setState((prev) => ({
      ...prev,
      deviceList: dList,
      selectedDevice,
      printer,
      labelData: !isEmpty(devices.labelData) ? devices.labelData : prev.labelData,
    }));
  }, [devices.registered, devices.defaultDevice, devices.labelData, state.qualityList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cachedValues = await ValueHelper.getCachedValues(
        [common.cacheValues.labelPrintQuality],
        auth.constants,
        null,
        auth.user?.tenant
      );
      if (cancelled) return;
      setState((prev) => ({
        ...prev,
        qualityList: cachedValues.labelPrintQualities ?? [],
      }));
    })();
    return () => { cancelled = true; };
  }, [auth.constants, auth.user?.tenant]);

  useEffect(() => {
    if (state.qualityList.length === 0) return;
    loadRecord();
  }, [loadRecord, state.qualityList.length]);

  const prevDeviceListRef = useRef(devices.deviceList);
  useEffect(() => {
    if (prevDeviceListRef.current !== devices.deviceList) {
      prevDeviceListRef.current = devices.deviceList;
      dispatch({ type: 'CREATE_ALERT', payload: { message: t('deviceListChange'), title: t('success'), severity: 'success' } });
      loadRecord();
    }
  }, [devices.deviceList, dispatch, loadRecord, t]);

  const changeValue = useCallback((e) => {
    const name = e.target.name;
    const value = e.target.value;
    setState((prev) => ({ ...prev, [name]: value, ready: false }));
  }, []);

  const changePrinter = useCallback(
    (event) => {
      const deviceList = devices?.deviceList ?? devices?.registered ?? [];
      const newPrinter = deviceList.find((x) => x.name?.toLowerCase() === event.target.value);
      if (!newPrinter) return;
      const qualitySel = state.qualityList.find((q) => q.value === newPrinter.quality);
      setState((prev) => ({
        ...prev,
        selectedDevice: { label: newPrinter.name, value: newPrinter.name.toLowerCase() },
        printer: { ...newPrinter, qualitySel },
        selectedTemplate: null,
        enablePreview: false,
      }));
    },
    [devices, state.qualityList]
  );

  const changeTemplate = useCallback(
    (event) => {
      const tpl = state.templateList.find(x => x._id === event.target.value);
      setState(prev => ({
        ...prev,
        selectedTemplate: tpl || null,
        enablePreview: !!tpl,
      }));
    },
    [state.templateList]
  );

  const buildLabelData = useCallback(
    (details) => {
      const labelConfig = state.printer?.labels?.find(l => l.type === devices.labelType);
      const count = labelConfig?.promptForCount ? (state.count ?? 1) : (labelConfig?.count ?? 1);
      const printerConfig = {
        density: state.printer.quality,
        pause: state.printer.pauseCount ?? 0,
        replica: state.printer.replica ?? 1,
        override: state.printer.override ?? false,
      };
      return {
        // Lets the server build QR links / format dates for fields a screen didn't send
        baseUrl: window.location.origin,
        dateFormat: auth.user?.dateFormat,
        ...details,
        ...printerConfig,
        templateId: state.selectedTemplate?._id,
        count: count,
      };
    },
    [state.printer, state.selectedTemplate, state.count, devices.labelType, auth.user?.dateFormat]
  );

  const validate = useCallback(() => {
    const err = {};
    const labelConfig = state.printer?.labels?.find(l => l.type === devices.labelType);
    if (labelConfig?.promptForCount) {
      if (state.count == null || state.count === '') err[t('count')] = t('required');
      if (state.count < 1) err[t('count')] = t('moreThanZero');
    }
    if (isEmpty(state.printer?.quality)) err[t('quality')] = t('required');
    if (!state.selectedTemplate) err[t('template')] = t('required');
    if (isEmpty(state.selectedDevice)) err[t('selectedDevice')] = t('required');
    if (!isEmpty(err)) {
      dispatch({ type: 'CREATE_ERROR', payload: err });
      return false;
    }
    if (!isEmpty(state.errors)) dispatch({ type: 'CLEAR_ERROR' });
    return true;
  }, [state.count, state.printer, state.selectedTemplate, state.selectedDevice, state.errors, devices.labelType, t, dispatch]);

  const handlePrint = useCallback(
    async (printer, sendToPrint) => {
      const registered = devices?.registered ?? [];
      const liveDevice = registered.find(d => d.uid === printer.uid && d.name === printer.name) || printer;
      await DeviceHelper.print(liveDevice, sendToPrint);
      dispatch({ type: 'CREATE_ALERT', payload: { message: t('labelPrintSuccess'), title: t('success'), severity: 'success' } });
    },
    [t, dispatch, devices?.registered]
  );

  const checkReady = useCallback(
    async (option) => {
      setState((prev) => ({ ...prev, enablePrint: false }));
      const valid = validate();
      if (!valid) {
        setState((prev) => ({ ...prev, enablePrint: true }));
        return;
      }

      const labelData = state.labelData;
      const isMulti = labelData != null && labelData !== '' && Array.isArray(labelData) && labelData.length > 1;

      try {
        if (isMulti) {
          const labelDetails = labelData.map((data) => buildLabelData(data));
          const { data: multiData } = await axios.post('api/multilabel', labelDetails);
          setState((prev) => ({ ...prev, sendToPrint: multiData }));
          if (option === 'print') {
            await handlePrint(state.printer, multiData);
          } else if (option === 'preview') {
            const dims = state.selectedTemplate
              ? `${(state.selectedTemplate.labelWidth / (state.selectedTemplate.dpi || 203)).toFixed(2)}x${(state.selectedTemplate.labelHeight / (state.selectedTemplate.dpi || 203)).toFixed(2)}`
              : null;
            setState((prev) => ({ ...prev, printPreview: true, dimensions: dims }));
          }
        } else {
          const singleData = Array.isArray(labelData) ? labelData[0] : labelData;
          const labelDetails = buildLabelData(singleData ?? {});
          const { data: resultData, status } = await axios.post('/api/label', labelDetails);
          setState((prev) => ({ ...prev, sendToPrint: resultData }));
          if (option === 'print') {
            await handlePrint(state.printer, resultData);
          } else if (option === 'preview') {
            const dims = state.selectedTemplate
              ? `${(state.selectedTemplate.labelWidth / (state.selectedTemplate.dpi || 203)).toFixed(2)}x${(state.selectedTemplate.labelHeight / (state.selectedTemplate.dpi || 203)).toFixed(2)}`
              : null;
            setState((prev) => ({ ...prev, printPreview: true, dimensions: dims }));
          }
          if (status !== 200) {
            dispatch({ type: 'CREATE_ALERT', payload: { message: t('error'), title: t('error'), severity: 'error' } });
          }
        }
        dispatch({ type: 'CREATE_ALERT', payload: { message: t('labelPrintSuccess'), title: t('success'), severity: 'success' } });
      } catch (err) {
        dispatch({ type: 'CREATE_ALERT', payload: { message: err?.message ?? t('error'), title: t('error'), severity: 'error' } });
      } finally {
        setState((prev) => ({ ...prev, enablePrint: true }));
      }
    },
    [state.labelData, state.printer, state.selectedTemplate, validate, buildLabelData, handlePrint, t, dispatch]
  );

  const handleClose = useCallback(() => {
    dispatch({ type: 'TOGGLE_PRINTDIALOG' });
  }, [dispatch]);

  const buttonStack = useMemo(
    () => [
      <Button
        key="print-close-btn"
        onClick={handleClose}
        sx={{ color: 'secondary.main' }}
        data-cy="labels-dialog-close"
      >
        Close
      </Button>,
      ...(deviceLabelConfig?.labelPreview ? [<Button
        key="print-preview-btn"
        disabled={!state.enablePreview}
        onClick={() => checkReady('preview')}
        sx={{ color: 'info.main' }}
        data-cy="labels-dialog-preview"
      >
        {t('preview')}
      </Button>] : []),
      <Button
        key="print-submit-btn"
        onClick={() => checkReady('print')}
        sx={{ color: 'primary.main' }}
        disabled={!state.enablePrint || !state.selectedTemplate}
        data-cy="labels-dialog-print"
      >
        {t('print')} {t('label')}
      </Button>,
    ],
    [state.enablePreview, state.enablePrint, state.selectedTemplate, deviceLabelConfig, handleClose, checkReady, t]
  );

  const { count, deviceList, errors, printer, selectedDevice, printPreview } = state;

  return (
    <ModalDialog
      buttonStack={buttonStack}
      title={t('labelPrintService')}
      toggleDialog={handleClose}
      dialogStatus={devices.openPrintDialog}
      fullWidth
      maxWidth="sm"
      data-cy="labels-dialog"
    >
      <Grid container spacing={3}>
        <Grid size={8}>
          <SelectField
            error={!!errors[t('selectedDevice')]}
            helperText={errors[t('selectedDevice')]}
            disabled={isEmpty(deviceList)}
            label={t('printer')}
            name="printer"
            options={deviceList}
            onChange={changePrinter}
            value={selectedDevice?.value ?? ''}
            defaultValue={!isEmpty(devices.defaultDevice) ? devices.defaultDevice.name?.toLowerCase() : ''}
            data-cy="labels-field-printer"
          />
        </Grid>
        <Grid size={4}>
          <Typography data-cy="labels-quality-text">
            {t('quality')}: {printer?.qualitySel != null && printer.qualitySel !== '' ? printer.qualitySel.label : ''}
          </Typography>
          {!!printer?.pauseCount && <Typography data-cy="labels-pause-count">{t('pauseCount')}: {printer.pauseCount}</Typography>}
        </Grid>
        <Grid size={12}>
          <hr />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid size={8}>
          <SelectField
            label={t('template')}
            name="template"
            options={templateOptions}
            onChange={changeTemplate}
            value={state.selectedTemplate?._id ?? ''}
            defaultValue=""
            fullWidth
            disabled={state.templatesLoading}
            data-cy="labels-field-template"
          />
        </Grid>
        {!!deviceLabelConfig?.promptForCount && (
          <Grid size={4}>
            <TextField
              value={count}
              onChange={changeValue}
              name="count"
              size="medium"
              fullWidth
              disabled={false}
              label={t('count')}
              type="number"
              error={!!errors[t('count')]}
              helperText={errors[t('count')]}
              data-cy="labels-field-count"
            />
          </Grid>
        )}
      </Grid>
      {printPreview && (
        <Grid container spacing={3}>
          <Grid size={12}>
            <PrintPreview
              labelData={state.sendToPrint}
              quality={printer?.qualitySel != null ? printer.qualitySel.label : ''}
              dimensions={state.dimensions}
              altText={state.selectedTemplate?.name ?? ''}
              data-cy="labels-print-preview"
            />
          </Grid>
        </Grid>
      )}
    </ModalDialog>
  );
}

export default Labels;
